/**
 * Ezoic Privacy Banner Controller
 * 
 * Prevents duplicate privacy banners and persists user consent across page navigations.
 * Uses a singleton pattern to maintain state across component remounts in React SPA.
 */

const CONSENT_STORAGE_KEY = 'ezoic_privacy_consent_given';

let bannerObserver: MutationObserver | null = null;
let isInitialized = false;
let consentGiven = false;

// Check if user already gave consent in previous session
const hasUserConsented = (): boolean => {
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

// Save user consent to localStorage
const saveConsent = () => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
    consentGiven = true;
  } catch (error) {
    console.warn('Failed to save Ezoic consent:', error);
  }
};

// Watch for user interaction with privacy banner
const watchForConsentAction = (bannerElement: HTMLElement) => {
  // Listen for clicks on banner buttons (accept/close)
  const buttons = bannerElement.querySelectorAll('button, a, [role="button"]');
  buttons.forEach((button) => {
    button.addEventListener('click', saveConsent, { once: true });
  });

  // Also watch for banner removal (user dismissed it)
  const removalObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node === bannerElement) {
          saveConsent();
          removalObserver.disconnect();
        }
      });
    });
  });

  if (bannerElement.parentNode) {
    removalObserver.observe(bannerElement.parentNode, { childList: true });
  }
};

/**
 * Initialize banner control as a singleton
 * Only runs once, persists across component remounts
 */
export const initEzoicBannerControl = () => {
  if (isInitialized) return;

  isInitialized = true;
  consentGiven = hasUserConsented();

  // Watch for privacy banners being added to DOM
  bannerObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const element = node as HTMLElement;
        const isPrivacyBanner = 
          element.id?.includes('privacy') ||
          element.className?.includes('privacy') ||
          element.className?.includes('consent') ||
          element.className?.includes('gatekeeper') ||
          element.textContent?.includes('Privacy & Transparency');

        if (isPrivacyBanner) {
          if (consentGiven) {
            // User already consented - remove banner immediately
            element.remove();
          } else {
            // First time - allow banner and watch for user consent
            consentGiven = true; // Prevent duplicates in same session
            watchForConsentAction(element);
          }
        }
      });
    });
  });

  if (document.body) {
    bannerObserver.observe(document.body, { childList: true, subtree: true });
  }
};

/**
 * Cleanup function - keeps observer alive as singleton
 * Only disconnects when explicitly switching away from Ezoic
 */
export const cleanupEzoicBannerControl = () => {
  // Observer stays alive across component remounts
};

/**
 * Force cleanup when switching to a different ad provider
 */
export const forceCleanupEzoicBannerControl = () => {
  if (bannerObserver) {
    bannerObserver.disconnect();
    bannerObserver = null;
    isInitialized = false;
    consentGiven = false;
  }
};
